import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { b } from 'utils/i18nUtils';
import { useSelector } from 'hooks';

import { getCustomerInfoFormValues } from 'modules/form/selectors';

import { Input, Spinner } from 'components/ui';
import { useDisplayNameFormatter } from 'components/DisplayNameFormatter/useDisplayNameFormatter';
import classNames from 'classnames';

type TypeSignatureProps = {
  onChange?: (signature?: HTMLCanvasElement) => void;
};

const LATIN_FONT = 'OoohBaby';

const FONTS = {
  [LATIN_FONT]: `${process.env.ASSETS_URL}/public/themes/default/fonts/OooohBaby/${LATIN_FONT}.ttf`,
};

const TypeSignature: React.FC<TypeSignatureProps> = ({ onChange }) => {
  const intl = useIntl();

  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const user = useSelector(getCustomerInfoFormValues);
  const formatDisplayName = useDisplayNameFormatter();

  const [name, setName] = React.useState(formatDisplayName(user));
  const [isFontLoaded, setIsFontLoaded] = React.useState(false);

  const loadFont = React.useCallback(async () => {
    const font = new FontFace(LATIN_FONT, `url(${FONTS[LATIN_FONT]})`);
    await font.load();
    document.fonts.add(font);
    setIsFontLoaded(true);
  }, []);

  React.useEffect(() => {
    loadFont();
  }, []);

  React.useEffect(() => {
    if (isFontLoaded && canvasRef.current && name.trim()) {
      paint(name);
      onChange?.(canvasRef.current);
    }
  }, [isFontLoaded, name, onChange]);


  const paint = React.useCallback((text: string) => {
    if (canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      if (context) {
        // Set canvas dimensions to match its displayed size for better quality
        canvasRef.current.width = canvasRef.current.offsetWidth;
        canvasRef.current.height = canvasRef.current.offsetHeight;

        context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

        if (text) {
          const fonts = getComputedStyle(canvasRef.current).getPropertyValue('--font-theme') ?? 'Arial';
          context.font = `bold 64px "${LATIN_FONT}", ${fonts}`;
          context.textAlign = 'center';
          context.fillStyle = '#000000';
          context.textBaseline = 'middle';

          context.fillText(
            // Text is padded with two spaces to avoid characters slightly going out of bonds
            ` ${text.trim()} `,
            canvasRef.current.width / 2,
            canvasRef.current.height / 2,
            canvasRef.current.width
          );
        }
      }
    }
  }, []);

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setName(name);
    paint(name);
    onChange?.(name.trim() ? canvasRef.current : undefined);
  }, [onChange, paint]);

  // This effects is triggered on mounting to inform parent that we already have a signature ready (when display name is defined)
  React.useLayoutEffect(() => {
    if (canvasRef.current && name.trim()) {
      paint(name);
      onChange?.(canvasRef.current);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-l">
      <Input
        value={name}
        inputClassName="!pt-8"
        onChange={handleChange}
        data-testid="signature-type"
        label={intl.formatMessage({ id: 'signature.completeName', defaultMessage: 'Complete Name' })}
        labelClassName={classNames('kiosk:peer-focus:-translate-y-7 kiosk:top-8', name && 'kiosk:-translate-y-7')}
      />
      {!isFontLoaded && <Spinner />}
      {isFontLoaded && <canvas ref={canvasRef} aria-hidden className="!h-auto kiosk:!h-96 aspect-video w-full bg-gray-100 rounded-input" />}
      <p className="text-xs">
        <FormattedMessage
          id="signature.agreement"
          // eslint-disable-next-line max-len
          defaultMessage={'By clicking on <b>"Confirm"</b>, I agree that the digital version of my signature can be used on all check-in related documents to mark my consent.'}
          values={{ b }}
        />
      </p>
    </div>
  );
};

export default TypeSignature;
